import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { FallbackLoader } from './common/PageLoader/PageLoader';
import { ErrorBoundary } from './pages/Errors/ErrorBoundary';
import Base from './pages/Layout/Base';
import BasePage from './pages/Layout/BasePage';

const ErrorPage = lazy(() => import(/* webpackChunkName: "root" */'./pages/Errors/ErrorPage'));
const Organizations = lazy(() => import(/* webpackChunkName: "root" */'./pages/Organization'));
const Externals = lazy(() => import(/* webpackChunkName: "root" */'./pages/External'));
const Dashboard = lazy(() => import(/* webpackChunkName: "root" */'./pages/Dashboard'));

const OrganizationDetails = lazy(() => import(/* webpackChunkName: "organization" */'./pages/Organization/Details'));
const SubscriptionDetails = lazy(() => import(/* webpackChunkName: "organization" */'./pages/Organization/Subscription'));
const Settings = lazy(() => import(/* webpackChunkName: "organization" */'./pages/Settings/SettingsPanel'));

const backtestNodes: any = {
    INPUT: true,
    TEXTAREA: true
}
const backtestLocalNames: any = {
    input: true,
    textarea: true
}

const RouteError = (props: any) => {
    return (
        <BasePage>
            <ErrorBoundary>
                <Suspense fallback={<FallbackLoader />}>
                    <Routes location={props.location}>
                        <Route path="/error/:status" element={<ErrorPage {...props} />} />
                        <Route path="*" element={<Navigate to={'/error/404'} replace />} />
                    </Routes>
                </Suspense>
            </ErrorBoundary>
        </BasePage>
    )
}

export const AppRoutes = (props: Record<string,unknown>) => {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onKeyDown = (e: any) => {
            if (e.keyCode === 8) {
                const target = e.target;

                if (target) {
                    if (target.nodeName && backtestNodes[target.nodeName] === true)
                        return;
                    if (target.localName && backtestLocalNames[target.localName] === true)
                        return;
                }

                if (location.pathname && location.pathname !== '/')
                    navigate(-1);
            }
        };

        document.addEventListener('keydown', onKeyDown, false);
        return () => document.removeEventListener('keydown', onKeyDown, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let currentKey = '/';

    if (!location.hash) {
        if (location.pathname) {
            const pathNames = location.pathname.split('/');
            currentKey = pathNames && pathNames.length > 0 ? pathNames[1] : '/';
        }
    }
    else if (location.hash.startsWith("#subs-")) {
        const id = location.hash.substring(6);
        location.pathname = `/subscription/${id}`;
        location.hash = '';
        currentKey = 'subscription';
    }

    if (currentKey === "error") {
        return (<RouteError {...props} location={location} />);
    }
    else {
        return (
            <Base>
                <ErrorBoundary>
                    <Suspense fallback={<FallbackLoader />}>
                        <Routes location={location}>
                            <Route path='/' element={<Organizations location={location} />} />
                            <Route path='/organization/:id/:type?' element={<OrganizationDetails />} />
                            <Route path='/subscription/:id' element={<SubscriptionDetails />} />
                            <Route path='/settings' element={<Settings />} />
                            <Route path='/externals' element={<Externals />} />
                            <Route path='/dashboard' element={<Dashboard />} />
                            <Route path="*" element={<Navigate to={'/'} replace />} />
                        </Routes>
                    </Suspense>
                </ErrorBoundary>
            </Base>
        )
    }
};
